import React from "react";
import { Box } from "@mui/joy";
import { DragIndicatorIcon, DeleteOutlineIcon } from "../../../styles/StyledMUI";
import type { DropResult } from "@hello-pangea/dnd";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { ConfirmationModalContext } from "../../../contexts/app/confirmation";
import type { MailPlaceholderDefinition } from "api/mailTemplates/MailTemplate.typed";
import {
  MAIL_PLACEHOLDER_TYPE_BOOLEAN,
  MAIL_PLACEHOLDER_TYPE_URL,
} from "api/mailTemplates/MailTemplate.typed";
import { mailEditorBlockCardSx } from "../../../styles/globalStyles";
import { mailPlaceholderPresentableLabel } from "./mailEditorShared";
import {
  appendPhToBlockParts,
  MAIL_BLOCK_LABELS,
  MAIL_BODY_CONDITIONAL_CONDITION_FIELD_KEY,
  MAIL_BODY_CONDITIONAL_ELSE_FIELD_KEY,
  MAIL_BODY_CONDITIONAL_IF_FIELD_KEY,
  MAIL_BODY_RICH_FIELD_KEY,
} from "./mailBodyBlocks";
import {
  mailPlaceholderBodyHrefScheme,
  mailPlaceholderDefByKey,
  mailPlaceholderKeyAllowedForValueTypes,
  mailPlaceholderValueTypesForConditionKeyField,
  mailPlaceholderValueTypesForFreeformTemplate,
  mailPlaceholderValueTypesForRichBody,
} from "./mailPlaceholderContext";
import MailBodyBlockFields from "./display/MailBodyBlockFields";
import { applyTextFieldUpdate, patchBlock } from "./mailBlockEditorModel";
import type { MailBodyBlock } from "./mailBodyBlocks";
import { insertMailPhChipAtCaretInContentEditable } from "./mailContentEditable";
import {
  mailLinePartsToConditionFieldKey,
  mergeAdjacentTextParts,
  parseHtmlToMailLineParts,
  parseMailPlaceholderKeyFromToken,
  sanitizeMailConditionFieldStorageKey,
  serializeMailLinePartsToSubject,
} from "./mailLineParts";
import {
  ensureMailContactItems,
  mailPlaceholderValueTypesForContactKind,
  parseMailContactItemFieldKey,
} from "./mailContactBlock";
import {
  MAIL_GOOGLE_REVIEW_FIELD_KEY,
  mailPlaceholderValueTypesForGoogleReview,
} from "./mailGoogleReviewBlock";

export type MailBodyBlockEditorHandle = {
  insertToken: (token: string) => void;
};

type FocusField = { blockId: string; field: string };

const MailBodyBlockEditor = React.forwardRef<
  MailBodyBlockEditorHandle,
  {
    blocks: MailBodyBlock[];
    onChange: (blocks: MailBodyBlock[]) => void;
    canEdit: boolean;
    booleanConditionPlaceholders: MailPlaceholderDefinition[];
    placeholderLabelCatalog: MailPlaceholderDefinition[];
  }
>(function MailBodyBlockEditor(props, ref) {
  const { blocks, onChange, canEdit, booleanConditionPlaceholders, placeholderLabelCatalog } =
    props;

  const presentablePhLabel = React.useCallback(
    (key: string) => mailPlaceholderPresentableLabel(key, placeholderLabelCatalog),
    [placeholderLabelCatalog],
  );
  const { showConfirmation } = React.useContext(ConfirmationModalContext);
  const lastFocusRef = React.useRef<FocusField | null>(null);
  const fieldRefs = React.useRef<Map<string, HTMLElement>>(new Map());

  const setFieldRef = React.useCallback((key: string, el: HTMLElement | null) => {
    if (el) {
      fieldRefs.current.set(key, el);
    } else {
      fieldRefs.current.delete(key);
    }
  }, []);

  const trackFocus = React.useCallback((blockId: string, field: string) => {
    lastFocusRef.current = { blockId, field };
  }, []);

  const insertToken = React.useCallback(
    (token: string) => {
      if (!canEdit) return;

      const phKeyFromToken = parseMailPlaceholderKeyFromToken(token);
      const focus = lastFocusRef.current;
      const blk = focus ? blocks.find((b) => b.id === focus.blockId) : null;

      const richSurfaceFields = new Set<string>([
        MAIL_BODY_RICH_FIELD_KEY,
        MAIL_BODY_CONDITIONAL_IF_FIELD_KEY,
        MAIL_BODY_CONDITIONAL_ELSE_FIELD_KEY,
        MAIL_BODY_CONDITIONAL_CONDITION_FIELD_KEY,
      ]);

      if (blk && phKeyFromToken && focus?.field && richSurfaceFields.has(focus.field)) {
        if (placeholderLabelCatalog.length > 0) {
          const allowed =
            focus.field === MAIL_BODY_CONDITIONAL_CONDITION_FIELD_KEY
              ? mailPlaceholderValueTypesForConditionKeyField()
              : mailPlaceholderValueTypesForRichBody();

          if (
            !mailPlaceholderKeyAllowedForValueTypes(
              placeholderLabelCatalog,
              phKeyFromToken,
              allowed,
            )
          ) {
            return;
          }
        }

        const richKey = `${focus.blockId}:${focus.field}`;
        const richEl = fieldRefs.current.get(richKey);

        if (richEl instanceof HTMLDivElement && richEl.isContentEditable) {
          const canUseSurface =
            (focus.field === MAIL_BODY_RICH_FIELD_KEY &&
              (blk.type === "title" ||
                blk.type === "footer" ||
                (blk.type === "paragraph" && blk.variant === "boxed") ||
                (blk.type === "paragraph" && "content" in blk && blk.content.format === "rich"))) ||
            (blk.type === "paragraph" &&
              "content" in blk &&
              blk.content.format === "conditional" &&
              (focus.field === MAIL_BODY_CONDITIONAL_IF_FIELD_KEY ||
                focus.field === MAIL_BODY_CONDITIONAL_ELSE_FIELD_KEY ||
                focus.field === MAIL_BODY_CONDITIONAL_CONDITION_FIELD_KEY));

          if (canUseSurface) {
            insertMailPhChipAtCaretInContentEditable(
              richEl,
              phKeyFromToken,
              "none",
              presentablePhLabel(phKeyFromToken),
              mailPlaceholderBodyHrefScheme(phKeyFromToken, placeholderLabelCatalog),
            );
            const nextParts = mergeAdjacentTextParts(parseHtmlToMailLineParts(richEl.innerHTML));

            if (blk.type === "title" || blk.type === "footer") {
              onChange(patchBlock(blocks, blk.id, { ...blk, parts: nextParts }));

              return;
            }

            if (blk.type === "paragraph" && blk.variant === "boxed") {
              onChange(patchBlock(blocks, blk.id, { ...blk, parts: nextParts }));

              return;
            }

            if (blk.type === "paragraph" && blk.content.format === "conditional") {
              if (focus.field === MAIL_BODY_CONDITIONAL_IF_FIELD_KEY) {
                onChange(
                  patchBlock(blocks, blk.id, {
                    ...blk,
                    content: { ...blk.content, ifParts: nextParts },
                  }),
                );

                return;
              }

              if (focus.field === MAIL_BODY_CONDITIONAL_ELSE_FIELD_KEY) {
                onChange(
                  patchBlock(blocks, blk.id, {
                    ...blk,
                    content: { ...blk.content, elseParts: nextParts },
                  }),
                );

                return;
              }

              if (focus.field === MAIL_BODY_CONDITIONAL_CONDITION_FIELD_KEY) {
                const nextKey = sanitizeMailConditionFieldStorageKey(
                  mailLinePartsToConditionFieldKey(nextParts),
                );

                onChange(
                  patchBlock(blocks, blk.id, {
                    ...blk,
                    content: { ...blk.content, conditionField: nextKey },
                  }),
                );

                return;
              }
            }

            onChange(
              patchBlock(blocks, blk.id, {
                ...blk,
                content: { format: "rich", parts: nextParts },
              }),
            );

            return;
          }
        }
      }

      const contactKind = focus?.field ? parseMailContactItemFieldKey(focus.field) : null;
      const contactFocus = focus;

      if (blk?.type === "contact" && contactKind && phKeyFromToken && contactFocus) {
        if (placeholderLabelCatalog.length > 0) {
          if (
            !mailPlaceholderKeyAllowedForValueTypes(
              placeholderLabelCatalog,
              phKeyFromToken,
              mailPlaceholderValueTypesForContactKind(contactKind),
              { allowUnknownKey: true },
            )
          ) {
            return;
          }
        }

        const richKey = `${contactFocus.blockId}:${contactFocus.field}`;
        const richEl = fieldRefs.current.get(richKey);

        if (richEl instanceof HTMLDivElement && richEl.isContentEditable) {
          insertMailPhChipAtCaretInContentEditable(
            richEl,
            phKeyFromToken,
            "none",
            presentablePhLabel(phKeyFromToken),
          );
          const nextParts = mergeAdjacentTextParts(parseHtmlToMailLineParts(richEl.innerHTML));
          const nextValue = serializeMailLinePartsToSubject(nextParts);

          onChange(
            patchBlock(blocks, blk.id, {
              ...blk,
              items: ensureMailContactItems(blk.items).map((item) =>
                item.kind === contactKind ? { ...item, enabled: true, value: nextValue } : item,
              ),
            }),
          );

          return;
        }
      }

      if (
        blk?.type === "googleReview" &&
        contactFocus?.field === MAIL_GOOGLE_REVIEW_FIELD_KEY &&
        phKeyFromToken
      ) {
        if (placeholderLabelCatalog.length > 0) {
          if (
            !mailPlaceholderKeyAllowedForValueTypes(
              placeholderLabelCatalog,
              phKeyFromToken,
              mailPlaceholderValueTypesForGoogleReview(),
              { allowUnknownKey: true },
            )
          ) {
            return;
          }
        }

        const richKey = `${contactFocus.blockId}:${MAIL_GOOGLE_REVIEW_FIELD_KEY}`;
        const richEl = fieldRefs.current.get(richKey);

        if (richEl instanceof HTMLDivElement && richEl.isContentEditable) {
          insertMailPhChipAtCaretInContentEditable(
            richEl,
            phKeyFromToken,
            "none",
            presentablePhLabel(phKeyFromToken),
          );
          const nextParts = mergeAdjacentTextParts(parseHtmlToMailLineParts(richEl.innerHTML));
          const nextValue = serializeMailLinePartsToSubject(nextParts);

          onChange(patchBlock(blocks, blk.id, { ...blk, href: nextValue }));

          return;
        }
      }

      const refKey = focus ? `${focus.blockId}:${focus.field}` : null;
      const el = refKey ? fieldRefs.current.get(refKey) : null;

      if (el && focus && (el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement)) {
        if (phKeyFromToken && placeholderLabelCatalog.length > 0) {
          const isFreeform = focus.field === "html";

          if (
            isFreeform &&
            !mailPlaceholderKeyAllowedForValueTypes(
              placeholderLabelCatalog,
              phKeyFromToken,
              mailPlaceholderValueTypesForFreeformTemplate(),
              { allowUnknownKey: true },
            )
          ) {
            return;
          }
        }

        const start = el.selectionStart ?? 0;
        const end = el.selectionEnd ?? 0;
        const val = el.value;
        const nextVal = val.slice(0, start) + token + val.slice(end);

        applyTextFieldUpdate(focus.blockId, focus.field, nextVal, blocks, onChange);

        requestAnimationFrame(() => {
          el.focus();
          const pos = start + token.length;
          el.setSelectionRange(pos, pos);
        });

        return;
      }

      const phKey = parseMailPlaceholderKeyFromToken(token);

      if (!phKey || blocks.length === 0) return;

      if (placeholderLabelCatalog.length > 0) {
        const def = mailPlaceholderDefByKey(placeholderLabelCatalog, phKey);

        if (def?.type === MAIL_PLACEHOLDER_TYPE_BOOLEAN) {
          return;
        }

        if (def?.type === MAIL_PLACEHOLDER_TYPE_URL) {
          const last = blocks[blocks.length - 1];

          if (last?.type !== "html" && last?.type !== "contact" && last?.type !== "googleReview") {
            return;
          }
        }
      }

      const last = blocks[blocks.length - 1];
      const withPh = appendPhToBlockParts(last, phKey);

      if (withPh) {
        onChange(patchBlock(blocks, last.id, withPh));

        return;
      }
    },
    [blocks, canEdit, onChange, placeholderLabelCatalog],
  );

  React.useImperativeHandle(ref, () => ({ insertToken }), [insertToken]);

  const onDragEnd = React.useCallback(
    (result: DropResult) => {
      if (!result.destination || !canEdit) return;

      const from = result.source.index;
      const to = result.destination.index;

      if (from === to) return;

      const next = Array.from(blocks);
      const [removed] = next.splice(from, 1);
      next.splice(to, 0, removed);
      onChange(next);
    },
    [blocks, canEdit, onChange],
  );

  const removeAt = async (index: number) => {
    if (!canEdit) return;

    const ok = await showConfirmation("Retirer ce bloc", "Ce bloc sera supprimé du message.");

    if (!ok) return;

    onChange(blocks.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="mail-body-blocks">
          {(droppableProvided) => (
            <Box ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
              {blocks.map((block, index) => (
                <Draggable
                  key={block.id}
                  draggableId={block.id}
                  index={index}
                  isDragDisabled={!canEdit}
                >
                  {(draggableProvided, snapshot) => (
                    <Box
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      sx={{
                        ...(mailEditorBlockCardSx as Record<string, unknown>),
                        ...(snapshot.isDragging
                          ? {
                              boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
                              opacity: 0.95,
                            }
                          : {}),
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mb: 0.75,
                          flexWrap: "wrap",
                        }}
                      >
                        {canEdit && (
                          <Box
                            {...draggableProvided.dragHandleProps}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: { xs: 40, sm: 36 },
                              minHeight: { xs: 40, sm: 36 },
                              cursor: "grab",
                              color: "text.secondary",
                              borderRadius: "8px",
                              flexShrink: 0,
                              touchAction: "none",
                              "&:active": { cursor: "grabbing" },
                            }}
                            aria-label="Glisser pour réordonner le bloc"
                          >
                            <DragIndicatorIcon sx={{ fontSize: { xs: 22, sm: 20 } }} />
                          </Box>
                        )}
                        <DarkTypography
                          level="body-xs"
                          colorVariant="muted"
                          sx={{ flex: 1, minWidth: 0 }}
                        >
                          {MAIL_BLOCK_LABELS[block.type]}
                        </DarkTypography>
                        {canEdit && (
                          <IconButtonCTA
                            aria-label="Supprimer le bloc"
                            size="sm"
                            ctaVariant="danger"
                            onClick={() => removeAt(index)}
                          >
                            <DeleteOutlineIcon />
                          </IconButtonCTA>
                        )}
                      </Box>

                      <MailBodyBlockFields
                        block={block}
                        canEdit={canEdit}
                        onPatch={(next) => onChange(patchBlock(blocks, block.id, next))}
                        setFieldRef={setFieldRef}
                        trackFocus={trackFocus}
                        booleanConditionPlaceholders={booleanConditionPlaceholders}
                        presentablePhLabel={presentablePhLabel}
                        placeholderCatalog={placeholderLabelCatalog}
                      />
                    </Box>
                  )}
                </Draggable>
              ))}
              {droppableProvided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
});

MailBodyBlockEditor.displayName = "MailBodyBlockEditor";

export default MailBodyBlockEditor;
